import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './policy-admin.css';

// Type Imports
import { Policy, PolicyReviewHistory, PolicyListItem } from '@/types/policy';
import {
  getPolicyList,
  savePolicy,
  reviewAllPolicies,
  getPolicyAssignments,
  getDocPolicies,
  getDocProcedures,
  savePolicyDocument,
  deletePolicy
} from '@/services/policyService';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { getAuthUID, onAuthReady, isAuthLoaded } from '@/lib/authState';
import { PolicyAdminSkeletonLoader } from '@/app/pages/policy-admin/PolicyAdminSkeletonLoader';
import { useTenancy } from '@/contexts/TenancyContext';
import { Button } from '@/components/ui/button';
import { Download, Eye, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { DataTable } from '@/app/pages/policy-admin/components/datatable';
import { AddProcessDialog } from '@/app/pages/policy-admin/components/AddProcessDialog';
import { AddPolicyDialog } from '@/app/pages/policy-admin/components/AddPolicyDialog';
import { EditProcessDialog } from '@/app/pages/policy-admin/components/EditProcessDialog';
import { getColumns } from '@/app/pages/policy-admin/components/columns';
import { getProcesses, getProcess, Process, syncDocumentLibraryProcedures, getProcessByDocumentId, ensureProcessComponentIds, deleteProcess } from '@/services/processService';
import { deleteDocument } from '@/services/documentsService';
import PolicyEditDialog from '@/app/pages/policy-admin/PolicyEditDialog';
import PolicyHistoryDialog from '@/app/pages/policy-admin/PolicyHistoryDialog';
import { db } from '@/js/external-js/config/main_config';
import { getHelpContentByPage } from '@/services/HelpPopoverService';
import HelpPopoverHover from '@/components/HelpPopoverHover';
// import ReviewPolicyDialog from '@/app/pages/policy-admin/ReviewPolicyDialog'; // @TODO when we have time
import PrivacyPolicyDialog from '@/app/pages/policy-admin/PrivacyPolicyDialog';
import { getPrivacyPolicy, savePrivacyPolicy, PrivacyPolicy, getCollectionNotice, saveCollectionNotice, CollectionNotice } from '@/services/policyService';
import { downloadFromSDK } from '@/js/external-js/variableModules/downloadFromSDK.module';
import { firebaseConfig, storageRef } from '@/js/external-js/config/main_config';

interface PolicyAdminProps {
  isReadOnly?: boolean;
  /** Initial filter for category: 'all', 'policy', or 'procedure' */
  initialFilter?: 'all' | 'policy' | 'procedure';
}

export function PolicyAdmin({ isReadOnly = false, initialFilter }: PolicyAdminProps = {}) {
  const [uid, setUid] = useState<string | null>(isAuthLoaded() ? getAuthUID() : null);
  const [policyList, setPolicyList] = useState<Array<Policy | PolicyListItem>>([]);
  const { activeTenancy, userIdToName, isModuleEnabled, enabledModules } = useTenancy();
  const tenancyId = activeTenancy?.id || window.companyID;
  
  // URL search params for category filter (e.g., ?filter=procedure)
  // Props take precedence over URL params
  const [searchParams] = useSearchParams();
  const filterParam = searchParams.get('filter');
  const initialCategoryFilter: 'all' | 'policy' | 'procedure' = 
    initialFilter ? initialFilter :
    filterParam === 'procedure' ? 'procedure' : 
    filterParam === 'policy' ? 'policy' : 'all';

  // Privacy Policy Dialog State
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [privacyDialogMode, setPrivacyDialogMode] = useState<'view' | 'edit'>('view');
  const [privacyPolicy, setPrivacyPolicy] = useState<PrivacyPolicy | null>(null);
  const [privacyDialogInitialTab, setPrivacyDialogInitialTab] = useState<'view' | 'history'>('view');
  const [privacyDraft, setPrivacyDraft] = useState<string>('');
  const [privacyLoading, setPrivacyLoading] = useState<boolean>(false);
  const [privacySaving, setPrivacySaving] = useState<boolean>(false);
  const [privacyError, setPrivacyError] = useState<string | null>(null);

  // Collection Notice Dialog State
  const [collectionDialogOpen, setCollectionDialogOpen] = useState(false);
  const [collectionDialogMode, setCollectionDialogMode] = useState<'view' | 'edit'>('view');
  const [collectionNotice, setCollectionNotice] = useState<CollectionNotice | null>(null);
  const [collectionDialogInitialTab, setCollectionDialogInitialTab] = useState<'view' | 'history'>('view');
  const [collectionDraft, setCollectionDraft] = useState<string>('');
  const [collectionLoading, setCollectionLoading] = useState<boolean>(false);
  const [collectionSaving, setCollectionSaving] = useState<boolean>(false);
  const [collectionError, setCollectionError] = useState<string | null>(null);

  // Add Process Dialog State
  const [addProcessDialogOpen, setAddProcessDialogOpen] = useState(false);
  
  // Add Policy Dialog State
  const [addPolicyDialogOpen, setAddPolicyDialogOpen] = useState(false);
  
  // Edit Process Dialog State
  const [editProcessDialogOpen, setEditProcessDialogOpen] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);

  // Delete Confirmation Dialog State
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Policy | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load privacy policy on mount/tenancy change
  useEffect(() => {
    if (!activeTenancy?.id || ["", "Loading..."].includes(activeTenancy?.id)) return;
    setPrivacyLoading(true);
    getPrivacyPolicy(activeTenancy.id)
      .then((policy) => {
        setPrivacyPolicy(policy);
        setPrivacyDraft(decodeURIComponent(policy?.content || ''));
      })
      .catch((err) => setPrivacyError('Failed to load privacy policy'))
      .finally(() => setPrivacyLoading(false));

    // Load Collection Notice
    setCollectionLoading(true);
    getCollectionNotice(activeTenancy.id)
      .then((notice) => {
        setCollectionNotice(notice);
        setCollectionDraft(decodeURIComponent(notice?.content || ''));
      })
      .catch((err) => setCollectionError('Failed to load collection notice'))
      .finally(() => setCollectionLoading(false));
  }, [activeTenancy?.id]);

  const handlePrivacySave = async () => {
    // if (!privacyPolicy) {
    //   console.error('[PrivacyPolicy] No policy to save');
    //   return;
    // }
    setPrivacySaving(true);
    setPrivacyError(null);
    try {
      await savePrivacyPolicy(
        activeTenancy.id,
        encodeURIComponent(privacyDraft),
        uid!,
        privacyPolicy?.version || '1.0',
        privacyPolicy?.content || ''
      );
      toast.success('Privacy Policy saved');
      // Reload
      const updated = await getPrivacyPolicy(activeTenancy.id);
      setPrivacyPolicy(updated);
      setPrivacyDialogMode('view');
    } catch (err) {
      console.error('[PrivacyPolicy] Error whilst saving:', err)
      setPrivacyError('Failed to save privacy policy');
    } finally {
      setPrivacySaving(false);
    }
  };

  const handleCollectionSave = async () => {
    setCollectionSaving(true);
    setCollectionError(null);
    try {
      await saveCollectionNotice(
        activeTenancy.id,
        encodeURIComponent(collectionDraft),
        uid!,
        collectionNotice?.version || '1.0',
        collectionNotice?.content || ''
      );
      toast.success('Collection Notice saved');
      // Reload
      const updated = await getCollectionNotice(activeTenancy.id);
      setCollectionNotice(updated);
      setCollectionDialogMode('view');
    } catch (err) {
      console.error('[CollectionNotice] Error whilst saving:', err)
      setCollectionError('Failed to save collection notice');
    } finally {
      setCollectionSaving(false);
    }
  };

  // Deep link: if route is /policyadmin/:policyId open edit dialog for that policy
  const { policyId: routePolicyId } = useParams<{ policyId?: string }>();
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);
  useEffect(() => {
    const pid = (routePolicyId || '').trim();
    if (!pid || deepLinkHandled) return;
    // Ensure policies are loaded
    if (!Array.isArray(policyList) || policyList.length === 0) return;
    const match = policyList.find((p: any) => {
      const candidates = [String(p?.policyRef || ''), String(p?.id || ''), String(p?.policy_id || '')].filter(Boolean);
      return candidates.includes(pid);
    }) as any;
    if (!match) return;
    try {
      onActions.onEdit?.(match);
      setDeepLinkHandled(true);
    } catch (e) {
      console.warn('[PolicyAdmin] Deep link open failed', e);
    }
  }, [routePolicyId, policyList, deepLinkHandled]);

  // Inject synthetic policy rows
  const syntheticRows = useMemo(() => {
    if (!activeTenancy?.id || ["", "Loading..."].includes(activeTenancy?.id)) return [];
    
    const isStarterPlan = window?.customerPlan?.toLowerCase() === 'starter';
    const hasPrivacyModule = isModuleEnabled('privacyact') || isModuleEnabled('pa');

    if (!isStarterPlan && !hasPrivacyModule) {
      return [];
    }

    const rows: any[] = [];

    // Privacy Policy Row
    rows.push({
      id: 'privacy_policy',
      policyRef: '',
      policy_name: 'Privacy Policy',
      policyCategory: 'General',
      policy_version: privacyPolicy?.version || '1.0',
      policy_status: 'Published',
      file_name: '',
      policy_history: {
        created_date: privacyPolicy?.lastEdited || new Date(),
        last_reviewed: privacyPolicy?.lastEdited || new Date(),
        policy_owner: '',
        reviewHistory: [],
        updated_date: privacyPolicy?.lastEdited || new Date(),
      },
      policy_metadata: {
        policyType: 'privacy',
        lastEdited: privacyPolicy?.lastEdited,
        lastEditedBy: privacyPolicy?.editHistory?.slice(-1)[0]?.editedBy,
      },
      relatedControls: [],
    });
    // DED-1231

    // Collection Notice Row
    rows.push({
      id: 'collection_notice',
      policyRef: '',
      policy_name: 'Collection Notice',
      policyCategory: 'General',
      policy_version: collectionNotice?.version || '1.0',
      policy_status: 'Published',
      file_name: '',
      policy_history: {
        created_date: collectionNotice?.lastEdited || new Date(),
        last_reviewed: collectionNotice?.lastEdited || new Date(),
        policy_owner: '',
        reviewHistory: [],
        updated_date: collectionNotice?.lastEdited || new Date(),
      },
      policy_metadata: {
        policyType: 'collection',
        lastEdited: collectionNotice?.lastEdited,
        lastEditedBy: collectionNotice?.editHistory?.slice(-1)[0]?.editedBy,
      },
      relatedControls: [],
    });

    return rows;
  }, [privacyPolicy, collectionNotice, privacyLoading, collectionLoading, isModuleEnabled, window?.customerPlan, activeTenancy]);

  // Compose table data with privacy row at the top
  const tableData = useMemo(() => {
    if (!activeTenancy?.id || ["", "Loading..."].includes(activeTenancy?.id)) return policyList;

    const rest = policyList.filter(p => p.id !== 'privacy_policy' && p.id !== 'collection_notice');
    const result = [...syntheticRows, ...rest];

    return result;
  }, [policyList, syntheticRows, isModuleEnabled, activeTenancy]);


  // State for History Modal
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedPolicyHistory, setSelectedPolicyHistory] = useState<PolicyReviewHistory[]>([]);
  const handleRevertReview = () => { };

  // State for Edit Modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);

  // State for modals

  // State for review dialog
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewDialogPolicy, setReviewDialogPolicy] = useState<Policy | null>(null);
  const [reviewDialogHistory, setReviewDialogHistory] = useState<PolicyReviewHistory[]>([]);
  const [reviewDialogLoading, setReviewDialogLoading] = useState(false);

  // Controls the visibility of the alert dialog for reviewing policies
  const [showConfirmReviewAlert, setShowConfirmReviewAlert] = useState<boolean>(false);

  const navigate = useNavigate();

  const isEnterprise = activeTenancy?.plan === 'enterprise' || activeTenancy?.plan === 'Enterprise Plan';

  // TODO: Implement these
  const isMobile = false;
  const isExporting = false;

  const onActions = {
    userIdToName,
    onRead: (row: Policy) => {
      console.log('[PolicyAdmin]', 'onRead', row);
      // Navigate to Policy Reader with policy ID
      // Handle both regular policies (policyRef) and document library policies (id/policy_id)
      const policyId = row.policyRef || row.id || (row as any).policy_id;
      if (!policyId) {
        console.error('No valid policy ID found for navigation:', row);
        return;
      }
      navigate(`/policy-reader/${policyId}`);
    },
    onViewHistory: (row: Policy) => {
      console.log('[PolicyAdmin]', 'onViewHistory', row);
      setSelectedPolicyHistory(Array.isArray(row.policy_history?.reviewHistory) ? row.policy_history?.reviewHistory?.reverse() : []);
      console.log('[PolicyAdmin]', 'reviewHistory', selectedPolicyHistory);
      setHistoryModalOpen(true);
    },
    onReview: (row: Policy) => {
      console.log('[PolicyAdmin]', 'onReview', row);
    },
    onEdit: (row: Policy) => {
      console.log('[PolicyAdmin]', 'onEdit', row);
      
      // Check if this is a process (from the processes collection)
      if ((row as any).isProcess && (row as any).processId) {
        const loadingToast = toast.loading('Loading process details...');
        getProcess(tenancyId, (row as any).processId).then((process) => {
          toast.dismiss(loadingToast);
          if (process) {
            setSelectedProcess(process);
            setEditProcessDialogOpen(true);
          } else {
            toast.error('Process not found');
          }
        }).catch((err) => {
          toast.dismiss(loadingToast);
          console.error('[PolicyAdmin] Failed to load process:', err);
          toast.error('Failed to load process');
        });
        return;
      }
      
      // For document library policies, open directly without loading toast
      if (row?.policy_metadata?.policyType === 'document') {
        setSelectedPolicy(row);
        setEditModalOpen(true);
        return;
      }
      
      // For default policies, show loading toast while fetching assignments
      const loadingToast = toast.loading('Loading policy details...');
      getPolicyAssignments(tenancyId, row.policyRef).then((userList) => {
        console.log('[PolicyAdmin]', 'getPolicyAssignments', userList);

        const temp = {
          ...row,
          assignedUsers: userList,
        };

        console.log('temp', temp)
        setSelectedPolicy(temp);
        setEditModalOpen(true);
        toast.dismiss(loadingToast);
      }).catch((err) => {
        toast.dismiss(loadingToast);
        console.error('[PolicyAdmin] Failed to load policy assignments:', err);
        toast.error('Failed to load policy details');
      });
    },
    onSave: (policy: Policy) => {
      console.log('[PolicyAdmin]', 'onSave', policy);
    },
    onDownload: async (row: Policy) => {
      console.log('[PolicyAdmin]', 'onDownload - full row data:', row);
      console.log('[PolicyAdmin]', 'onDownload - row.id:', row.id);
      console.log('[PolicyAdmin]', 'onDownload - row.docID:', (row as any).docID);
      console.log('[PolicyAdmin]', 'onDownload - row.file_name:', (row as any).file_name);
      console.log('[PolicyAdmin]', 'onDownload - row.fileName:', (row as any).fileName);
      console.log('[PolicyAdmin]', 'onDownload - policy_metadata:', row.policy_metadata);
      
      // For document library policies
      const docId = row.id || (row as any).docID || (row as any).policy_id;
      const fileName = (row as any).file_name || 
        row.policy_metadata?.file_name || 
        (row as any).fileName ||
        (row as any).displayName;
      
      console.log('[PolicyAdmin]', 'onDownload - resolved docId:', docId, 'fileName:', fileName);
      
      if (!docId || !fileName) {
        toast.error('Unable to download: missing document information');
        console.error('[PolicyAdmin]', 'Missing - docId:', docId, 'fileName:', fileName);
        return;
      }
      
      const loadingToast = toast.loading('Preparing download...');
      try {
        // Import Firebase storage utilities
        const { ref, getDownloadURL } = await import('firebase/storage');
        const { storage } = await import('@/lib/firebase');
        
        // Document library path pattern
        const storagePath = `/customers/${tenancyId}/keyDocuments/otherDocs/uploaded/${docId}/${fileName}`;
        const fileRef = ref(storage, storagePath);
        const downloadUrl = await getDownloadURL(fileRef);
        
        // Trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.dismiss(loadingToast);
        toast.success('Download started');
      } catch (error: any) {
        toast.dismiss(loadingToast);
        console.error('[PolicyAdmin] Download error:', error);
        if (error?.code === 'storage/object-not-found') {
          toast.error('File not found in storage');
        } else {
          toast.error('Failed to download file');
        }
      }
    },
    // Privacy Policy specific actions
    onPrivacyView: (_row) => {
      setPrivacyDialogOpen(true);
      setPrivacyDialogMode('view');
      setPrivacyDialogInitialTab('view');
    },
    // onPrivacyEdit: (_row) => {
    //   setPrivacyDialogOpen(true);
    //   setPrivacyDialogMode('edit');
    // },
    onPrivacyEdit: (_row) => {
      setPrivacyDraft(privacyPolicy?.content || '');
      setPrivacyDialogMode('edit');
      setPrivacyDialogInitialTab('view');
      setPrivacyDialogOpen(true);
    },
    onPrivacyHistory: (_row) => {
      setPrivacyDialogOpen(true);
      setPrivacyDialogMode('view');
      setPrivacyDialogInitialTab('history');
    },
    // Collection Notice specific actions
    onCollectionView: (_row) => {
      setCollectionDialogOpen(true);
      setCollectionDialogMode('view');
      setCollectionDialogInitialTab('view');
    },
    onCollectionEdit: (_row) => {
      setCollectionDraft(collectionNotice?.content || '');
      setCollectionDialogMode('edit');
      setCollectionDialogInitialTab('view');
      setCollectionDialogOpen(true);
    },
    onCollectionHistory: (_row) => {
      setCollectionDialogOpen(true);
      setCollectionDialogMode('view');
      setCollectionDialogInitialTab('history');
    },
    onDelete: (row: Policy) => {
      console.log('[PolicyAdmin]', 'onDelete', row);
      setItemToDelete(row);
      setShowDeleteConfirmDialog(true);
    },
  };


  const handleReviewAllPolicies = () => {
    toast.promise(reviewAllPolicies(tenancyId).then((result) => {
      setForceRefresh(forceRefresh + 1);
      return result;
    }), {
      loading: 'Refreshing policies...',
      success: 'Policies have been refreshed.',
      error: {
        render: (err: Error) => `There was an issue refreshing: ${err}`,
        duration: 10000,
      },
    });
  };

  // Handle delete for policies and processes
  const handleDeleteItem = async () => {
    if (!itemToDelete || !tenancyId) return;
    
    setIsDeleting(true);
    try {
      const isProcess = (itemToDelete as any).isProcess && (itemToDelete as any).processId;
      const itemName = itemToDelete.policy_name || (itemToDelete as any).name || 'Item';
      
      if (isProcess) {
        // For processes, we need to also delete the linked document from document library
        const processId = (itemToDelete as any).processId;
        
        // Get the full process to find the linked document ID
        const process = await getProcess(tenancyId, processId);
        const linkedDocId = process?.documentId || process?.documentLibraryRef?.documentId;
        
        // Delete the process first
        await deleteProcess(tenancyId, processId);
        
        // If there's a linked document, delete it too
        if (linkedDocId) {
          try {
            await deleteDocument(linkedDocId, tenancyId);
            console.log('[PolicyAdmin] Also deleted linked document:', linkedDocId);
          } catch (docErr) {
            // Log but don't fail if document deletion fails (might already be deleted)
            console.warn('[PolicyAdmin] Could not delete linked document:', docErr);
          }
        }
        
        toast.success(`Process "${itemName}" deleted successfully`);
      } else {
        // Determine if this is a document library policy or a default de.iterate policy
        const policyType = (itemToDelete as any).policy_metadata?.policyType;
        const isDocumentLibraryPolicy = policyType === 'document';
        
        console.log('[PolicyAdmin] Deleting policy:', {
          id: itemToDelete.id,
          policyRef: (itemToDelete as any).policyRef,
          policyType,
          isDocumentLibraryPolicy
        });
        
        if (isDocumentLibraryPolicy) {
          // Document library policy - use deleteDocument with docID
          const docId = itemToDelete.id || (itemToDelete as any).docID || (itemToDelete as any).policy_id;
          if (!docId) {
            throw new Error('Could not find document ID');
          }
          await deleteDocument(docId, tenancyId);
          toast.success(`Policy "${itemName}" deleted successfully`);
        } else {
          // Default de.iterate policy - use deletePolicy with policyRef or id
          const policyId = (itemToDelete as any).policyRef || itemToDelete.id || (itemToDelete as any).policy_id;
          if (!policyId) {
            throw new Error('Could not find policy ID');
          }
          await deletePolicy(tenancyId, policyId);
          toast.success(`Policy "${itemName}" deleted successfully`);
        }
      }
      
      // Refresh the list
      setForceRefresh(prev => prev + 1);
    } catch (error) {
      console.error('[PolicyAdmin] Delete error:', error);
      toast.error(`Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmDialog(false);
      setItemToDelete(null);
    }
  };

  useEffect(() => {
    if (!isAuthLoaded()) {
      const unsub = onAuthReady(() => {
        setUid(getAuthUID());
      });

      return unsub;
    }
  }, []);

  const [forceRefresh, setForceRefresh] = React.useState<number>(0);
  useEffect(() => {
    if (!tenancyId) return;

    Promise.all([
      getPolicyList(tenancyId),
      getDocPolicies(tenancyId, userIdToName),
      getDocProcedures(tenancyId, userIdToName),
      getProcesses(tenancyId)
    ]).then(async ([mainPolicies, docPolicies, docProcedures, processes]) => {
      // Ensure all existing processes have valid componentIds (PRO-1, PRO-2, etc.)
      try {
        const updatedCount = await ensureProcessComponentIds(tenancyId);
        if (updatedCount > 0) {
          console.log(`[PolicyAdmin] Assigned componentIds to ${updatedCount} processes`);
          // Re-fetch processes after updating componentIds
          processes = await getProcesses(tenancyId);
        }
      } catch (err) {
        console.error('[PolicyAdmin] Failed to ensure process componentIds:', err);
      }
      
      // Sync document library procedures to processes collection
      // This ensures all document library procedures have a corresponding process entry
      try {
        const syncedCount = await syncDocumentLibraryProcedures(tenancyId, docProcedures);
        if (syncedCount > 0) {
          console.log(`[PolicyAdmin] Synced ${syncedCount} document library procedures to processes collection`);
          // Re-fetch processes after sync
          const updatedProcesses = await getProcesses(tenancyId);
          processes = updatedProcesses;
        }
      } catch (err) {
        console.error('[PolicyAdmin] Failed to sync document library procedures:', err);
      }
      
      // Create a map of documentId -> process for quick lookup
      const docIdToProcess = new Map<string, Process>();
      processes.forEach((p: Process) => {
        if (p.documentId) {
          docIdToProcess.set(p.documentId, p);
        }
      });
      
      // Transform document library procedures to include process data
      // These will use the EditProcessDialog when edited
      const docProcedureRows = docProcedures.map((doc: any) => {
        const linkedProcess = docIdToProcess.get(doc.id);
        return {
          ...doc,
          // If there's a linked process, use its ID for editing
          processId: linkedProcess?.id,
          componentID: linkedProcess?.componentId || '', // PRO-1, PRO-2, etc.
          isProcess: true, // Flag to use EditProcessDialog
          isDocumentLibraryProcedure: true, // Flag to identify source
          processType: 'documented', // Document library procedures are always documented
          // Use process data if available for scheduling fields
          repeat: linkedProcess?.repeat,
          reviewDate: linkedProcess?.reviewDate || doc.nextReviewDate,
          nextReviewDate: linkedProcess?.nextReviewDate || doc.nextReviewDate,
        };
      });
      
      // Transform standalone processes (not linked to document library)
      const standaloneProcessRows = processes
        .filter((p: Process) => !p.documentId) // Only processes without a document link
        .map((p: Process) => ({
          id: p.id,
          processId: p.id,
          componentID: p.componentId || '', // PRO-1, PRO-2, etc.
          name: p.name,
          policy_name: p.name,
          policy_id: p.id,
          description: p.description,
          status: p.status || 'Draft',
          policy_status: p.status || 'Draft',
          owner: p.owner || '',
          category: 'Procedure',
          policyCategory: 'Procedure',
          version: '1.0',
          policy_version: '1.0',
          documentType: 'Procedure' as const,
          processType: p.type, // verbal, documented, undocumented
          businessUnit: p.businessUnit,
          last_reviewed: p.updatedAt || p.createdAt || new Date(),
          created: p.createdAt || new Date(),
          nextReviewDate: p.nextReviewDate || p.reviewDate || new Date(),
          policy_history: {
            policy_owner: p.owner || '',
            created_date: p.createdAt || new Date(),
            last_reviewed: p.updatedAt || p.createdAt || new Date(),
            nextReviewDate: p.nextReviewDate || p.reviewDate || new Date(),
          },
          policy_metadata: {
            policyType: 'document',
            documentCategory: 'procedure',
            lastEdited: p.updatedAt || p.createdAt || new Date(),
          },
          isProcess: true, // Flag to identify process rows
          relatedControls: p.relatedControls || [],
          procRefs: p.relatedControls || [],
          assignedUsers: [],
        }));
      
      // Combine: policies + doc policies + doc procedures (with process data) + standalone processes
      // Note: We use docProcedureRows instead of docProcedures to include process linking
      setPolicyList([...mainPolicies, ...docPolicies, ...docProcedureRows, ...standaloneProcessRows] as any);
      console.log('[policyService]', 'policyList', { mainPolicies, docPolicies, docProcedureRows, standaloneProcessRows, processes });
    });
  }, [tenancyId, userIdToName, forceRefresh]);



  const downloadPolicies = () => {

  };

  // NOTE: In the review dialog, add a note for backend devs about archiving
  const ReviewPolicyDialogNote = () => (
    <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mb-4">
      <strong>Backend Dev Note:</strong> When a review happens, archive all custom policies and policy changes for rollback functionality.
    </div>
  );

  const [helpContent, setHelpContent] = useState(null);
  useEffect(() => {
    getHelpContentByPage('policy.admin').then(setHelpContent);
  }, []);

  // PAGE LOGIC -------------------------------------
  if (!uid || !tenancyId) return <PolicyAdminSkeletonLoader />
  return (
    <div className="page-container">
      {/* TODO: Move this into a component */}
      <AlertDialog open={showConfirmReviewAlert} onOpenChange={setShowConfirmReviewAlert}>
        <AlertDialogContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0! bg-card"
        >
          <AlertDialogHeader className="sticky top-0 z-20 p-4 py-6 border-b border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <AlertDialogTitle className="text-lg font-medium">Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-muted-foreground">
              This will perform a review on all policies, which will reset the reading status for all users.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-t border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 flex! flex-col sm:flex-row justify-between! items-center! gap-2">
            <AlertDialogCancel className="w-full sm:w-1/2">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="w-full sm:w-1/2"
              onClick={() => { console.log('confirmed'); handleReviewAllPolicies(); }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="glass-card">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="title flex flex-row">
              {initialCategoryFilter === 'procedure' ? 'Processes' : 
               initialCategoryFilter === 'policy' ? 'Policies' : 'Policies & Processes'}
              <span className="flex items-center bg-transparent pl-2 rounded font-semibold text-center">
                <HelpPopoverHover config={helpContent?.['page.title']} />
              </span>
            </h2>
            <p className="subtitle">
              {initialCategoryFilter === 'procedure' 
                ? "Manage and administer your organization's processes" 
                : initialCategoryFilter === 'policy'
                ? "Manage and administer your organization's policies"
                : "Manage and administer your organization's policies and processes"}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            {/* Add Policy Button - show when viewing policies or all */}
            {(initialCategoryFilter === 'policy' || initialCategoryFilter === 'all') && !isReadOnly && (
              <Button
                variant="default"
                className="flex-1 sm:flex-none justify-center"
                onClick={() => setAddPolicyDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                <span className="whitespace-nowrap">Add Policy</span>
              </Button>
            )}
            {/* Add Process Button - show when viewing processes or all */}
            {(initialCategoryFilter === 'procedure' || initialCategoryFilter === 'all') && !isReadOnly && (
              <Button
                variant="default"
                className="flex-1 sm:flex-none justify-center"
                onClick={() => setAddProcessDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                <span className="whitespace-nowrap">Add Process</span>
              </Button>
            )}
            {/*TODO: Handle onClick handleReviewAllPolicies*/}
            {/*TODO: Handle state isReviewing*/}
            {!isEnterprise && initialCategoryFilter !== 'procedure' ? <Button
              variant="outline"
              className="flex-1 sm:flex-none justify-center"
              onClick={() => setShowConfirmReviewAlert(true)}
            >
              <Eye className="h-4 w-4 mr-2" />
              <span className="whitespace-nowrap">Review All Policies</span>
            </Button> : null}
            {/*TODO: Handle onClick handleExportAllPolicies*/}
            {/*TODO: Handle state isExporting*/}
            {/*<Button*/}
            {/*  variant="outline"*/}
            {/*  disabled={isExporting}*/}
            {/*  className="export-btn flex-1 sm:flex-none justify-center"*/}
            {/*>*/}
            {/*  <Download className="h-4 w-4 mr-2" />*/}
            {/*  <span className="whitespace-nowrap">Export All Policies</span>*/}
            {/*</Button>*/}
            {/* DED-1928 */}
          </div>
        </div>

        {/* Export progress */}
        {isExporting && (
          <div className="mb-6 p-4 bg-blue-50 rounded-md border border-blue-200">
            <h3 className="font-medium text-blue-800 mb-2">
              Exporting Policies: {exportProgress.current}/{exportProgress.total}
            </h3>
            <p className="text-sm text-blue-600">
              Policies are being exported. This may take a while. Please leave this tab open.
            </p>
          </div>
        )}

        <DataTable 
          pageSize={10} 
          columns={getColumns(onActions, isReadOnly, true)} 
          data={tableData} 
          initialCategoryFilter={initialCategoryFilter}
          onRowClick={(row) => {
            if (!row) return;
            if (row.id === 'privacy_policy') {
              setPrivacyDialogOpen(true);
              setPrivacyDialogMode('view');
            } else if (row.id === 'collection_notice') {
              setCollectionDialogOpen(true);
              setCollectionDialogMode('view');
            } else {
              onActions?.onEdit?.(row);
            }
          }} 
        />

      </div>
      {/* History Modal */}
      <PolicyHistoryDialog
        history={selectedPolicyHistory}
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        userName={async (userId) => {
          try {
            const userDoc = await db.collection('users').doc(userId).get();
            if (userDoc.exists) {
              const userData = userDoc.data();
              return `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || 'Unknown User';
            }
            return 'Unknown User';
          } catch (error) {
            console.error('Error fetching user name:', error);
            return 'Unknown User';
          }
        }}
        onRevertReview={handleRevertReview} // Add handler for revert review
      >
        {/*TODO: Review Action */}
        {/* <ReviewPolicyDialogNote /> */}
      </PolicyHistoryDialog>

      {/* Edit Modal */}
      <PolicyEditDialog
        UID={uid}
        companyID={tenancyId}
        policy={selectedPolicy}
        isOpen={editModalOpen}
        isReadOnly={isReadOnly}
        userIdToName={userIdToName}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedPolicy(null);
          // Use forceRefresh to trigger a full data reload including processes
          // This fixes DED-1630: clicking back after editing a policy was causing processes to disappear
          // because the previous implementation only fetched policies, not processes
          setForceRefresh(prev => prev + 1);
        }}
        onSave={async (updatedPolicy, fileToUpload) => {
          try {
            if (updatedPolicy.policy_metadata?.policyType?.toLowerCase() === 'document') {
              await savePolicyDocument(updatedPolicy, tenancyId);
            } else {
              await savePolicy(updatedPolicy, fileToUpload, tenancyId);
            }
            // Use forceRefresh to trigger a full data reload including processes
            // This ensures processes are not lost after saving (same fix as DED-1630)
            setForceRefresh(prev => prev + 1);
            toast.success('Policy saved successfully');
          } catch (error) {
            console.error('Error saving policy:', error);
            toast.error('Failed to save policy');
          }
        }}
      />

      {/* Privacy Policy Dialog */}
      <PrivacyPolicyDialog
        open={privacyDialogOpen}
        onClose={() => {
          setPrivacyDialogOpen(false);
          setPrivacyDialogInitialTab('view');
        }}
        policy={privacyPolicy}
        draft={privacyDraft}
        setDraft={setPrivacyDraft}
        mode={privacyDialogMode}
        setMode={setPrivacyDialogMode}
        loading={privacyLoading}
        saving={privacySaving}
        error={privacyError}
        onSave={handlePrivacySave}
        initialTab={privacyDialogInitialTab}
      />

      {/* Collection Notice Dialog */}
      <PrivacyPolicyDialog
        open={collectionDialogOpen}
        onClose={() => {
          setCollectionDialogOpen(false);
          setCollectionDialogInitialTab('view');
        }}
        policy={collectionNotice}
        draft={collectionDraft}
        setDraft={setCollectionDraft}
        mode={collectionDialogMode}
        setMode={setCollectionDialogMode}
        loading={collectionLoading}
        saving={collectionSaving}
        error={collectionError}
        onSave={handleCollectionSave}
        initialTab={collectionDialogInitialTab}
        title="Collection Notice"
      />

      {/* Add Policy Dialog */}
      <AddPolicyDialog
        open={addPolicyDialogOpen}
        onOpenChange={setAddPolicyDialogOpen}
        onSuccess={(newPolicy) => {
          // Inject the new policy into local state for immediate display
          if (newPolicy) {
            setPolicyList(prev => [newPolicy, ...prev]);
          }
        }}
      />

      {/* Add Process Dialog */}
      <AddProcessDialog
        open={addProcessDialogOpen}
        onOpenChange={setAddProcessDialogOpen}
        onSuccess={() => {
          // Refresh the process list
          setForceRefresh(prev => prev + 1);
        }}
      />

      {/* Edit Process Dialog */}
      <EditProcessDialog
        open={editProcessDialogOpen}
        onOpenChange={setEditProcessDialogOpen}
        process={selectedProcess}
        onSuccess={() => {
          // Refresh the process list
          setForceRefresh(prev => prev + 1);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {(itemToDelete as any)?.isProcess ? 'Process' : 'Policy'}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{itemToDelete?.policy_name || (itemToDelete as any)?.name || 'this item'}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteItem}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

}
